import { error } from '@sveltejs/kit';
import { command, query } from '$app/server';
import { getRequestEvent } from '$app/server';
import { supabaseAdmin } from '#lib/server/supabaseAdmin.js';
import type { LeaderboardEntry, Period, SLeaderboardEntry } from '~/types';

const getDateCutoff = (period: string) => {
  const day = new Date();
  if (period === 'week') day.setDate(day.getDate() - 7);
  else return new Date(0);
  return day;
};

type GetLeaderboardParams = {
  direction: string;
  period: string;
  id?: string;
  score?: string;
};

export const getLeaderboard = query(
  'unchecked',
  async (params: GetLeaderboardParams): Promise<SLeaderboardEntry[]> => {
    const { locals } = getRequestEvent();
    const { supabase } = locals;

    let q = supabase
      .from('leaderboard')
      .select('*')
      .order('score', { ascending: params.direction === 'asc' })
      .gt('date', getDateCutoff(params.period).toISOString());

    if (params.id && params.score) {
      q = q.neq('id', params.id);
      if (params.direction === 'asc') {
        q = q.gt('score', +params.score);
      } else {
        q = q.lt('score', +params.score);
      }
    }

    const { data } = await q.limit(10);
    return (data ?? []) as SLeaderboardEntry[];
  },
);

export const getLeaderboardEntry = query(
  'unchecked',
  async (id: string): Promise<SLeaderboardEntry | null> => {
    const { data: entry } = await supabaseAdmin
      .from('leaderboard')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (!entry) return null;

    const ranks = await Promise.all(
      (['day', 'week', 'all'] as Period[]).map(async (period) => {
        const rank = await supabaseAdmin
          .from('leaderboard')
          .select('*', { count: 'exact', head: true })
          .gte('score', entry.score ?? 0)
          .gt('date', getDateCutoff(period).toISOString());
        return rank.count;
      }),
    );

    const result = entry as unknown as SLeaderboardEntry;
    result.day_rank = ranks[0]!;
    result.week_rank = ranks[1]!;
    result.all_rank = ranks[2]!;
    return result;
  },
);

export const submitScore = command(
  'unchecked',
  async (game: Omit<SLeaderboardEntry, 'rank'>): Promise<SLeaderboardEntry> => {
    const { locals } = getRequestEvent();

    const entry = { ...game } as any;
    delete entry.day_rank;
    delete entry.week_rank;
    delete entry.all_rank;
    delete entry.local_rank;

    const response = await supabaseAdmin
      .from('leaderboard')
      .insert({ ...entry, userId: locals.userId })
      .select();

    if (response.error) error(500, response.error);

    const ranks = await Promise.all(
      (['day', 'week', 'all'] as Period[]).map(async (period) => {
        const rank = await supabaseAdmin
          .from('leaderboard')
          .select('*', { count: 'exact', head: true })
          .gte('score', game.score)
          .gt('date', getDateCutoff(period).toISOString());
        return rank.count;
      }),
    );

    const result = response.data[0] as SLeaderboardEntry;
    result.day_rank = ranks[0]!;
    result.week_rank = ranks[1]!;
    result.all_rank = ranks[2]!;

    return result;
  },
);
